import api from './api';

export interface CuentaCorrienteSaldo {
  nroCliente: number;
  nombre: string;
  telefono?: string | null;
  saldoTotal: number;
  saldoVencido: number;
  saldoVigente: number;
  ultimaActualizacion: string;
}

export interface CuentaCorrienteComprobante {
  compania: string;
  nroCliente: string;
  nombre: string;
  fecha: string;
  comprobante: string;
  numero: string;
  importe: number;
  saldoDeb: number;
  saldoHab: number;
  fechaVenc: string;
  saldo: number;
  nroTipoFormulario: string;
  moneda: number;
  nroFormulario: number;
  /** Empresa de origen; solo viene en la consulta unificada de la familia. */
  tenantCode?: string | null;
  tenantName?: string | null;
  /** false: el comprobante es de otra empresa y su nroFormulario no existe en la empresa actual. */
  esEmpresaActual?: boolean;
}

export interface EmpresaFamilia {
  tenantId: number;
  code: string;
  name: string;
  esActual: boolean;
}

/** Listado deuda por vencimiento (GET informes/deuda-por-vencimiento) */
export interface ClienteDeudaPorVencimiento {
  nroCliente: number;
  nombre: string;
  telefono?: string | null;
  direccion?: string | null;
  codigoRuta?: number | null;
  nombreRuta?: string | null;
  nroVendedor?: number | null;
  nombreVendedor?: string | null;
  deudaHastaVencimiento: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface MovimientosHistoricosResponse extends PaginatedResponse<MovimientoHistorico> {
  saldoInicial?: number | null;
}

export type SaldosSortBy = 'nombre' | 'monto' | 'longevidad';
export type SaldosSortDir = 'asc' | 'desc';

interface GetSaldosParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  nroVendedor?: number;
  sortBy?: SaldosSortBy;
  sortDir?: SaldosSortDir;
}

interface GetComprobantesParams {
  pageNumber?: number;
  pageSize?: number;
  unificado?: boolean;
}

export interface MovimientoHistorico {
  nroCliente: number | null;
  nombreCli: string | null;
  fechaEmision: string | null;
  fechaVencim: string | null;
  descripcionFor: string | null;
  descripcionTip: string | null;
  letra: string | null;
  nroSuc: number;
  nroComprobante: number | null;
  numero: string | null;
  importeTotal: number | null;
  importeCancelado: number | null;
  impCompDebe: number | null;
  impCompHaber: number | null;
  saldoComp: number | null;
  saldoCompDebe: number | null;
  saldoCompHaber: number | null;
  nroTipoFormulario: string | null;
  nroDoc: string | null;
  /** Nombre del formulario; "ANULADO" cuando el comprobante fue anulado */
  nombre: string | null;
  nroFormulario: number | null;
  tenantCode?: string | null;
  tenantName?: string | null;
  esEmpresaActual?: boolean;
}

interface GetMovimientosHistoricosParams {
  fechaDesde?: string;
  fechaHasta?: string;
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  unificado?: boolean;
}

export interface GetDeudaPorVencimientoParams {
  fechaHasta: string;
  codigoRuta?: number;
  nroVendedor?: number;
}

const parseResponse = <T>(data: unknown): T => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as T;
    } catch (error) {
      console.error('No se pudo parsear la respuesta de Cuenta Corriente:', error);
      throw new Error('Respuesta inválida del servidor de Cuenta Corriente');
    }
  }

  return data as T;
};

const getSaldos = async ({
  pageNumber = 1,
  pageSize = 20,
  searchTerm,
  nroVendedor,
  sortBy,
  sortDir
}: GetSaldosParams = {}): Promise<PaginatedResponse<CuentaCorrienteSaldo>> => {
  const params: Record<string, string | number> = {
    pageNumber,
    pageSize
  };
  
  if (searchTerm) {
    params.searchTerm = searchTerm;
  }

  if (nroVendedor != null && nroVendedor > 0) {
    params.nroVendedor = nroVendedor;
  }

  if (sortBy) {
    params.sortBy = sortBy;
  }

  if (sortDir) {
    params.sortDir = sortDir;
  }
  
  const response = await api.get('/api/CuentaCorriente/saldos', {
    params,
    headers: {
      Accept: 'text/plain'
    }
  });

  return parseResponse<PaginatedResponse<CuentaCorrienteSaldo>>(response.data);
};

const getFamilia = async (): Promise<EmpresaFamilia[]> => {
  const response = await api.get<EmpresaFamilia[]>('/api/CuentaCorriente/familia');
  return Array.isArray(response.data) ? response.data : [];
};

const getSaldoCliente = async (
  nroCliente: number,
  { unificado = false }: { unificado?: boolean } = {}
): Promise<CuentaCorrienteSaldo> => {
  const response = await api.get<CuentaCorrienteSaldo>(
    `/api/CuentaCorriente/cliente/${nroCliente}/saldo`,
    { params: unificado ? { unificado: true } : undefined }
  );
  return response.data;
};

const getComprobantesCliente = async (
  nroCliente: number,
  {
    pageNumber = 1,
    pageSize = 50,
    unificado = false
  }: GetComprobantesParams = {}
): Promise<PaginatedResponse<CuentaCorrienteComprobante>> => {
  const response = await api.get(`/api/CuentaCorriente/cliente/${nroCliente}`, {
    params: {
      pageNumber,
      pageSize,
      ...(unificado ? { unificado: true } : {})
    },
    headers: {
      Accept: 'text/plain'
    }
  });

  return parseResponse<PaginatedResponse<CuentaCorrienteComprobante>>(response.data);
};

const getMovimientosHistoricosCliente = async (
  nroCliente: number,
  {
    fechaDesde,
    fechaHasta,
    searchTerm,
    pageNumber = 1,
    pageSize = 10,
    unificado = false
  }: GetMovimientosHistoricosParams = {}
): Promise<MovimientosHistoricosResponse> => {
  const params: Record<string, string | number | boolean | undefined> = {
    pageNumber,
    pageSize
  };
  if (fechaDesde) params.fechaDesde = fechaDesde;
  if (fechaHasta) params.fechaHasta = fechaHasta;
  if (searchTerm) params.searchTerm = searchTerm;
  if (unificado) params.unificado = true;

  const response = await api.get(
    `/api/CuentaCorriente/cliente/${nroCliente}/movimientos-historicos`,
    {
      params,
      headers: { Accept: 'text/plain' }
    }
  );

  return parseResponse<MovimientosHistoricosResponse>(response.data);
};

export interface EstadisticasCuentaCorriente {
  totalClientesConCuentaCorriente: number;
  saldoTotalGeneral: number;
  saldoVencidoTotal: number;
  totalMovimientos: number;
  ultimaActualizacion: string;
  nroVendedorFiltrado: number | null;
  usuarioFiltrado: string | null;
}

const getEstadisticas = async (params?: {
  nroVendedor?: number | null;
}): Promise<EstadisticasCuentaCorriente> => {
  const query: Record<string, number> = {};
  if (params?.nroVendedor != null && params.nroVendedor > 0) {
    query.nroVendedor = params.nroVendedor;
  }
  const response = await api.get<EstadisticasCuentaCorriente>('/api/CuentaCorriente/estadisticas', {
    params: query,
  });
  return response.data;
};

const getDeudaPorVencimiento = async (
  params: GetDeudaPorVencimientoParams
): Promise<ClienteDeudaPorVencimiento[]> => {
  const query: Record<string, string | number> = {
    fechaHasta: params.fechaHasta
  };
  if (params.codigoRuta != null && params.codigoRuta !== undefined) {
    query.codigoRuta = params.codigoRuta;
  }
  if (params.nroVendedor != null && params.nroVendedor !== undefined) {
    query.nroVendedor = params.nroVendedor;
  }

  const response = await api.get('/api/CuentaCorriente/informes/deuda-por-vencimiento', {
    params: query,
    headers: { Accept: 'text/plain' }
  });

  return parseResponse<ClienteDeudaPorVencimiento[]>(response.data);
};

const cuentaCorrienteService = {
  getFamilia,
  getSaldos,
  getSaldoCliente,
  getComprobantesCliente,
  getMovimientosHistoricosCliente,
  getEstadisticas,
  getDeudaPorVencimiento
};

export default cuentaCorrienteService;
